import * as yup from 'yup';

import { IDroneReferenceData, IGetDroneReferenceDataResponse } from 'services/droneService';
import { DRONE_CONSTANTS, FORM_ERROR_MESSAGES } from 'internalConstants';
import { IGetIndividualDronesResponse, IGetOrganisationDronesResponse } from 'services/droneService/droneServiceTypes';

// helper functions for yup validation
// determine if selected model is pre-defined
const isModelSerialNumber = (modelKey: string, referenceData: IGetDroneReferenceDataResponse | null | undefined) => {
  let modelFound = false;
  referenceData?.value.DRONE_MODEL.map((item: IDroneReferenceData) => {
    if (item.casa_code === modelKey) {
      modelFound = true;
    }
  });
  return modelFound;
};

// get the serial number regex pattern for the given model
// const getSerialNumberPattern = (modelKey: string) => {
//   let serialPattern = '';
//   referenceData?.value.DRONE_MODEL.map(item => {
//     if (item.casa_code === modelKey) {
//       serialPattern = item?.casa_serialnumberpattern ?? '';
//     }
//   });
//   return new RegExp(serialPattern);
// };

const duplicateRPACheck = (serialNumber: string | undefined, context: any, isOrganisationRegistration: boolean, individualDrones: IGetIndividualDronesResponse | null | undefined, organisationDrones: IGetOrganisationDronesResponse | null | undefined) => {
  // validate serial number does not already exist within the current application
  // TODO: Has to be a better way to access the current form values
  // other than context.from[1].value.drones...
  if (serialNumber) {
    if (context.from[1].value.drones.filter(drone => drone.id !== context.parent.id).find(drone => drone.serialNumber === serialNumber)) {
      return false;
    }
    const currentDroneId = context.from[1].value.drones.find(drone => drone.id === context.parent.id).droneId;
    // validate serial number does not already exist within other drones registered to the individual or organisation
    if (!isOrganisationRegistration && individualDrones?.value.value.find(drone => drone['drone.casa_serialnumber'] === serialNumber && drone['drone.casa_droneid'] !== currentDroneId)) {
      return false;
    }
    if (isOrganisationRegistration && organisationDrones?.value.value.find(drone => drone['drone.casa_serialnumber'] === serialNumber && drone['drone.casa_droneid'] !== currentDroneId)) {
      return false;
    }
  }
  return true;
};

// determine if the model has serial number prefixes defined
const isCheckSerialNumberPrefix = (modelKey: string, referenceData: IGetDroneReferenceDataResponse | null | undefined, checkPrefix: boolean) => {
  //console.log(checkPrefix);
  // if (!checkPrefix) {
  //   return false;
  // }
  if (!isModelSerialNumber(modelKey, referenceData)) {
    return false;
  }
  const serialNumberPrefix = referenceData?.value.DRONE_MODEL.find(drone => drone.casa_code === modelKey)?.casa_serialnumberprefix;
  return serialNumberPrefix != null && serialNumberPrefix.trim().length > 0;
};

// build serial nmuber prefix regex pattern
const buildSerialNumberPrefixPattern = (modelKey: string, referenceData: IGetDroneReferenceDataResponse | null | undefined) => {
  let serialPrefixPattern = '';
  const serialNumberPrefix = referenceData?.value.DRONE_MODEL.find(drone => drone.casa_code === modelKey)?.casa_serialnumberprefix;
  if (serialNumberPrefix != null && serialNumberPrefix.trim().length > 0) {
    serialPrefixPattern = '^' + serialNumberPrefix.replace(/,/g, '|^').replace(/ /g, '');
  }
  return new RegExp(serialPrefixPattern, 'i');
};

// determine if the model has serial number prefixes defined
const isCheckSerialNumberLength = (modelKey: string, referenceData: IGetDroneReferenceDataResponse | null | undefined) => {
  if (!isModelSerialNumber(modelKey, referenceData)) {
    return false;
  }
  const serialNumberLength = referenceData?.value.DRONE_MODEL.find(drone => drone.casa_code === modelKey)?.casa_serialnumberlength;
  return serialNumberLength != null && serialNumberLength > 0;
};

const getSerialNumberLength = (modelKey: string, referenceData: IGetDroneReferenceDataResponse | null | undefined) => {
  const serialNumberLength = referenceData?.value.DRONE_MODEL.find(drone => drone.casa_code === modelKey)?.casa_serialnumberlength;
  return serialNumberLength ? serialNumberLength : 0;
};

const droneValidationSchema = (
  referenceData: IGetDroneReferenceDataResponse | null | undefined,
  isOrganisationRegistration: boolean,
  individualDrones: IGetIndividualDronesResponse | null | undefined,
  organisationDrones: IGetOrganisationDronesResponse | null | undefined,
  checkSerialNumberPrefix: boolean) => yup.array().of(
  yup.object().shape({
    manufacturerCode: yup.string()
      .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
    manufacturer: yup.string()
      .max(50, FORM_ERROR_MESSAGES.LENGTH_MAX)
      .when('manufacturerCode', {
        is: DRONE_CONSTANTS.OTHER_MANUFACTURER_CODE,
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
      }),
    modelCode: yup.string()
      .when('manufacturerCode', {
        is: (value?: string) => value !== DRONE_CONSTANTS.OTHER_MANUFACTURER_CODE && value !== DRONE_CONSTANTS.HOME_BUILT_MANUFACTURER_CODE,
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
      })
      .when('manufacturerCode', {
        is: DRONE_CONSTANTS.DJI_MANUFACTURER_CODE,
        then: yup.string().test('check-not-other-model', FORM_ERROR_MESSAGES.INVALID_MODEL_DJI_OTHER, async (value) => {
          return !value?.endsWith(DRONE_CONSTANTS.OTHER_MODEL_CODE_EXT);
        })
      }),
    model: yup.string()
      .max(50, FORM_ERROR_MESSAGES.LENGTH_MAX)
      .when('modelCode', {
        is: (value?: string) => value?.endsWith(DRONE_CONSTANTS.OTHER_MODEL_CODE_EXT),
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
      })
      .when('manufacturerCode', {
        is: (value?: string) => !value || value && value === DRONE_CONSTANTS.OTHER_MANUFACTURER_CODE,
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
      }),
    categoryCode: yup.string()
      .when('modelCode', {
        is: (value?: string) => value?.endsWith(DRONE_CONSTANTS.OTHER_MODEL_CODE_EXT),
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
      })
      .when('manufacturerCode', {
        is: DRONE_CONSTANTS.OTHER_MANUFACTURER_CODE,
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
      })
      .when('manufacturerCode', {
        is: DRONE_CONSTANTS.HOME_BUILT_MANUFACTURER_CODE,
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
      }),
    weightClassCode: yup.string()
      .when('categoryCode', {
        is: (value?: string) => value && value !== DRONE_CONSTANTS.AIRSHIP_CATEGORY_CODE,
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
      }),
    intendedUse: yup.string()
      .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
    serialNumber: yup.string()
      .test('is-duplicate-rpa', FORM_ERROR_MESSAGES.DUPLICATE_RPA, async (value, context) => {
        return duplicateRPACheck(value, context, isOrganisationRegistration, individualDrones, organisationDrones);
      })
      // .when('modelCode', (value: string, schema: yup.StringSchema) => value && isModelSerialNumber(value) ? schema.matches(getSerialNumberPattern(value), FORM_ERROR_MESSAGES.PATTERN_ERROR) : schema)
      .when('manufacturerCode', {
        is: (value?: string) => value !== DRONE_CONSTANTS.DJI_MANUFACTURER_CODE,
        then: yup.string()
          .when('modelCode', (value: string, schema: yup.StringSchema) => value && isCheckSerialNumberPrefix(value, referenceData, checkSerialNumberPrefix) ? schema.matches(buildSerialNumberPrefixPattern(value, referenceData), FORM_ERROR_MESSAGES.PATTERN_ERROR) : schema)
          .when('modelCode', (value: string, schema: yup.StringSchema) => value && isCheckSerialNumberLength(value, referenceData) ? schema.length(getSerialNumberLength(value, referenceData), FORM_ERROR_MESSAGES.PATTERN_ERROR) : schema)
      })
      .when('manufacturerCode', {
        is: DRONE_CONSTANTS.DJI_MANUFACTURER_CODE,
        then: yup.string().matches(new RegExp('^(?!CASA-)', 'i'), FORM_ERROR_MESSAGES.PATTERN_ERROR)
      })
      .when('noSerialNumber', {
        is: (value?: string) => !value,
        then: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
      }),
    registeredOverseas: yup.string()
      .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
  })
);

export default droneValidationSchema;